import { useEffect, useState } from "react";
import { ChevronDown, Sparkles } from "lucide-react";
import { useLang } from "@/i18n";
import PageHeader from "@/components/PageHeader";
import MandalaDivider from "@/components/MandalaDivider";
import { paramparaList } from "@/mockData";

function FeaturedCard({ saint, i, lv, badge }) {
  return (
    <article
      data-testid={`parampara-featured-${i}`}
      className="card-soft reveal p-8 lg:p-10 flex flex-col md:flex-row gap-8 items-center md:items-start relative"
    >
      {saint.is_current && (
        <span className="absolute top-6 right-6 text-[10px] tracking-[0.22em] uppercase bg-[#7B1818] text-[#F4EBDB] px-3 py-1 rounded-full">
          {badge}
        </span>
      )}
      <div className="w-44 h-44 lg:w-52 lg:h-52 shrink-0 rounded-full overflow-hidden ring-1 ring-[#E5DAC7] shadow-lg bg-[#F4EBDB]">
        {saint.image ? (
          <img
            src={saint.image}
            alt={lv(saint, "name")}
            className={`w-full h-full ${saint.image.endsWith(".png") ? "object-contain" : "object-cover"}`}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center font-display text-4xl text-[#D4AF37]">॥ श्री ॥</div>
        )}
      </div>
      <div className="flex-1 text-center md:text-left">
        <div className="eyebrow">{lv(saint, "period")}</div>
        <h3 className="mt-3 font-display text-2xl lg:text-3xl xl:text-[34px] text-[#7B1818] leading-tight">
          {saint.seq ? `${saint.seq}. ` : ""}{lv(saint, "name")}
        </h3>
        <div className="mt-4 flex md:justify-start justify-center">
          <MandalaDivider className="w-40" />
        </div>
        <p className="mt-4 text-[15px] text-[#5C4D42] leading-[1.85]">{lv(saint, "description")}</p>
      </div>
    </article>
  );
}

function SectionHeadingBlock({ roman, title, sub, sectionLabel }) {
  return (
    <div className="reveal text-center">
      <div className="eyebrow flex items-center justify-center gap-2">
        <Sparkles size={12} className="text-[#D4AF37]" /> {sectionLabel} {roman}
      </div>
      <h2 className="mt-3 font-display text-2xl sm:text-3xl lg:text-4xl text-[#2A2118] leading-[1.15]">{title}</h2>
      {sub && <p className="mt-3 text-[14px] text-[#5C4D42] max-w-2xl mx-auto">{sub}</p>}
      <div className="mt-5 flex justify-center"><MandalaDivider /></div>
    </div>
  );
}

function NameList({ items, testidPrefix, lv }) {
  return (
    <ol className="grid sm:grid-cols-2 gap-x-8 gap-y-2">
      {items.map((s, i) => (
        <li
          key={s.id}
          data-testid={`${testidPrefix}-item-${i}`}
          className="flex items-baseline gap-3 py-2 border-b border-dashed border-[#E5DAC7]/70 last:border-b-0"
        >
          <span className="w-7 text-right font-display text-sm text-[#D4AF37] tabular-nums">
            {s.seq ? String(s.seq).padStart(2, "0") : String(i + 1).padStart(2, "0")}
          </span>
          <div className="flex-1">
            <div className="font-display text-[16.5px] text-[#2A2118] leading-tight">{lv(s, "name")}</div>
            {s.description && (
              <div className="text-[11.5px] tracking-[0.06em] text-[#8a6a3a] mt-0.5">{lv(s, "description")}</div>
            )}
          </div>
        </li>
      ))}
    </ol>
  );
}

function Connector() {
  return (
    <div className="flex flex-col items-center gap-4 py-2">
      <div className="w-px h-10 bg-gradient-to-b from-[#D4AF37] to-transparent" />
      <div className="w-2.5 h-2.5 rounded-full bg-[#D4AF37]" />
      <div className="w-px h-10 bg-gradient-to-b from-[#D4AF37] to-transparent rotate-180" />
    </div>
  );
}

export default function Parampara() {
  const [list, setList] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const { t, lv } = useLang();
  const p = t.parampara;

  useEffect(() => {
    setList(paramparaList);
  }, []);

  const pre = list.filter(s => s.section === "pre_madhwa");
  const post = list.filter(s => s.section === "post_madhwa");
  const bh = list.filter(s => s.section === "bhimanakatte");

  const madhwa = post.find(s => s.is_featured);
  const postRolled = post.filter(s => !s.is_featured);
  const bhList = bh.filter(s => !s.is_featured);
  const previous = bh.find(s => s.is_featured && !s.is_current);
  const current = bh.find(s => s.is_current);

  return (
    <div data-testid="parampara-page">
      <PageHeader eyebrow={p.eyebrow} title={p.title} sub={p.sub} />

      <section className="section-pad">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-14">

          <SectionHeadingBlock roman="I" sectionLabel={p.section} title={p.s1Title} sub={p.s1Sub} />
          {pre.length > 0 && (
            <div data-testid="parampara-pre-madhwa" className="card-soft reveal p-6 lg:p-8">
              <NameList items={pre} testidPrefix="parampara-pre" lv={lv} />
            </div>
          )}

          <Connector />

          <SectionHeadingBlock roman="II" sectionLabel={p.section} title={p.s2Title} sub={p.s2Sub} />

          {madhwa && <FeaturedCard saint={madhwa} i="top" lv={lv} badge={p.presentBadge} />}

          {postRolled.length > 0 && (
            <div className="card-soft reveal p-6 lg:p-8">
              <button
                data-testid="parampara-toggle-btn"
                onClick={() => setExpanded(v => !v)}
                className="w-full flex items-center justify-between gap-4 text-left group"
              >
                <div>
                  <div className="eyebrow flex items-center gap-2">
                    <Sparkles size={12} className="text-[#D4AF37]" />
                    {p.rolledEyebrow}
                  </div>
                  <h3 className="mt-2 font-display text-2xl lg:text-3xl text-[#2A2118]">
                    {postRolled.length} {p.rolledTitle}
                  </h3>
                  <p className="mt-1 text-[13.5px] text-[#5C4D42]">{p.rolledSub}</p>
                </div>
                <div
                  className={`w-11 h-11 rounded-full bg-[#F4EBDB] text-[#7B1818] flex items-center justify-center transition-transform ${expanded ? "rotate-180" : ""}`}
                  aria-label={expanded ? "Collapse" : "Expand"}
                >
                  <ChevronDown size={18} />
                </div>
              </button>

              <div
                data-testid="parampara-rolled-list"
                className={`overflow-hidden transition-[max-height,opacity] duration-500 ${expanded ? "opacity-100" : "opacity-0"}`}
                style={{ maxHeight: expanded ? "3000px" : "0px" }}
              >
                <div className="mt-6 pt-6 border-t border-[#E5DAC7]">
                  <NameList items={postRolled} testidPrefix="parampara-rolled" lv={lv} />
                </div>
              </div>
            </div>
          )}

          <Connector />

          <SectionHeadingBlock roman="III" sectionLabel={p.section} title={p.s3Title} sub={p.s3Sub} />

          {bhList.length > 0 && (
            <div data-testid="parampara-bhimanakatte" className="card-soft reveal p-6 lg:p-8">
              <NameList items={bhList} testidPrefix="parampara-bh" lv={lv} />
            </div>
          )}

          {previous && <FeaturedCard saint={previous} i="previous" lv={lv} badge={p.presentBadge} />}
          {current && <FeaturedCard saint={current} i="current" lv={lv} badge={p.presentBadge} />}
        </div>
      </section>
    </div>
  );
}
